import React, { useMemo } from "react";
import BaseChart from "../../components/ui/BaseChart";

const fullUsd = (n) =>
  `$${Number(n ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const compactUsd = (n) => {
  const v = Number(n ?? 0);
  if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
};

// `breakdown` = array of { teamId, name, color, amountUsd, count }, largest first.
const TeamBreakdownChart = ({ breakdown, mode = "revenue" }) => {
  const isRevenue = mode === "revenue";

  const { categories, points, total } = useMemo(() => {
    if (!breakdown?.length) return { categories: [], points: [], total: 0 };
    const rows = breakdown.filter((t) => (t.amountUsd ?? 0) > 0);
    const sum = rows.reduce((a, t) => a + t.amountUsd, 0);
    return {
      categories: rows.map((t) => t.name),
      points: rows.map((t) => ({ y: t.amountUsd, color: t.color, count: t.count })),
      total: Math.round(sum * 100) / 100,
    };
  }, [breakdown]);

  const chartOptions = useMemo(() => {
    if (!points.length) return null;
    return {
      chart: {
        type: "bar",
        height: Math.max(180, points.length * 46 + 40),
        spacing: [10, 16, 6, 6],
        backgroundColor: "transparent",
        style: { fontFamily: "Poppins, sans-serif" },
      },
      title: { text: null },
      xAxis: {
        categories,
        lineColor: "#e5e7eb",
        tickLength: 0,
        labels: { style: { fontSize: "11px", color: "#475569", fontWeight: "500" } },
      },
      yAxis: {
        title: { text: null },
        gridLineColor: "#f3f4f6",
        min: 0,
        labels: {
          formatter() {
            return compactUsd(this.value);
          },
          style: { fontSize: "10px", color: "#9ca3af" },
        },
      },
      legend: { enabled: false },
      tooltip: {
        backgroundColor: "#1e293b",
        style: { color: "#f8fafc", fontSize: "11px" },
        borderWidth: 0,
        borderRadius: 6,
        useHTML: true,
        pointFormatter() {
          const pct = total > 0 ? ((this.y / total) * 100).toFixed(1) : "0";
          return `<span style="color:${this.color}">●</span> <b>${this.category}</b><br/>${fullUsd(this.y)} · <b>${pct}%</b><br/><span style="opacity:.7">${this.count} invoices</span>`;
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 5,
          borderWidth: 0,
          pointPadding: 0.08,
          groupPadding: 0.08,
          dataLabels: {
            enabled: true,
            formatter() {
              return compactUsd(this.y);
            },
            style: {
              fontSize: "10px",
              fontWeight: "600",
              color: "#475569",
              textOutline: "none",
            },
          },
        },
      },
      series: [{ name: isRevenue ? "Revenue" : "Expense", data: points }],
    };
  }, [points, categories, total, isRevenue]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h4 className="text-sm font-semibold text-gray-700">
            {isRevenue ? "Revenue" : "Expense"} by Team
          </h4>
          <p className="text-[11px] text-gray-400 mt-0.5">Each client rolls up to its team · USD</p>
        </div>
        {total > 0 && (
          <span className="text-[11px] text-gray-400">
            Total <span className="font-semibold text-gray-700">{fullUsd(total)}</span>
          </span>
        )}
      </div>

      {chartOptions ? (
        <BaseChart options={chartOptions} className="w-full" />
      ) : (
        <p className="text-sm text-gray-400 py-16 text-center">No data for this period.</p>
      )}
    </div>
  );
};

export default TeamBreakdownChart;
